import { Button, CloudImage, Heading, Text } from "@fluidattacks/design";
import { Fragment, useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { ConsentActions } from "./styles";

import { ResultContainer, ResultWrapper } from "pages/cli-oauth-result/styles";
import { BigScreenLogin } from "pages/login/big-screen";
import {
  CLI_ERROR_PATH,
  approveCliOauthRequest,
  cliOauthContext,
  denyCliOauthRequest,
  fetchCliOauthRequest,
  readCliOauthRequestIdFromUrl,
  stashCliOauthRequestId,
} from "utils/cli-oauth";
import { Logger } from "utils/logger";

type TStage = "consent" | "loading" | "login";

/*
 * Consent screen the backend authorization endpoint redirects to. It carries only
 * an opaque request id; the authorization parameters stay server-side. An explicit
 * approval is what makes the flow safe with a `same_site=lax` session cookie,
 * which a plain top-level GET would otherwise carry on its own.
 */
const CliOauth: React.FC = (): JSX.Element => {
  const [stage, setStage] = useState<TStage>("loading");
  const [clientName, setClientName] = useState("");
  const requestId = readCliOauthRequestIdFromUrl();

  useEffect((): void => {
    if (requestId === null) {
      window.location.assign(CLI_ERROR_PATH);

      return;
    }
    // Survives the round trip through Auth0 when the user has to log in first.
    stashCliOauthRequestId(requestId);

    const load = async (id: string): Promise<void> => {
      try {
        const request = await fetchCliOauthRequest(id);
        if (request === "unauthenticated") {
          setStage("login");

          return;
        }
        if (request === null) {
          window.location.assign(CLI_ERROR_PATH);

          return;
        }
        setClientName(request.clientName);
        setStage("consent");
      } catch (error) {
        Logger.error("CLI OAuth request lookup failed", { error });
        window.location.assign(CLI_ERROR_PATH);
      }
    };

    void load(requestId);
  }, [requestId]);

  const handleApprove = useCallback((): void => {
    if (requestId === null) {
      return;
    }
    setStage("loading");
    void approveCliOauthRequest(requestId);
  }, [requestId]);

  /*
   * Refusing has to reach the CLI too, or it sits waiting out its own timeout for
   * an answer that was already given.
   */
  const handleCancel = useCallback((): void => {
    if (requestId === null) {
      window.location.assign(CLI_ERROR_PATH);

      return;
    }
    setStage("loading");
    void denyCliOauthRequest(requestId);
  }, [requestId]);

  if (stage === "login") {
    return (
      <Fragment>
        <Helmet>
          <title>{"Log in | Fluid Attacks"}</title>
        </Helmet>
        <cliOauthContext.Provider value={true}>
          <BigScreenLogin />
        </cliOauthContext.Provider>
      </Fragment>
    );
  }

  if (stage === "consent") {
    return (
      <Fragment>
        <Helmet>
          <title>{"Authorize sign in | Fluid Attacks"}</title>
        </Helmet>
        <ResultWrapper>
          <ResultContainer>
            <CloudImage
              alt={"Fluid Attacks Logo"}
              publicId={"integrates/login/loginLogoExtended"}
              width={"220px"}
            />
            <Heading fontWeight={"bold"} size={"lg"} textAlign={"center"}>
              {"Authorize sign in"}
            </Heading>
            <Text size={"lg"} textAlign={"center"}>
              {`${clientName} is asking to sign in as you. Approve only if you just started this sign-in yourself.`}
            </Text>
            <ConsentActions>
              <Button onClick={handleCancel} variant={"ghost"}>
                {"Cancel"}
              </Button>
              <Button onClick={handleApprove} variant={"primary"}>
                {"Approve"}
              </Button>
            </ConsentActions>
          </ResultContainer>
        </ResultWrapper>
      </Fragment>
    );
  }

  return <p>{"Completing sign in..."}</p>;
};

export { CliOauth };
